/**
 * Helpers for inline-editing RELATION fields in the table.
 *
 * Relation columns are identified by their `fieldType` string:
 *   - to-one:  ends with "Data"      e.g. "BrandData"
 *   - to-many: ends with "ListData"  e.g. "TagListData"
 * The target entity name is the prefix (e.g. "Brand", "Tag").
 *
 * A relation row value is shaped `{ data: {...} | [...] }`; each item is the
 * related record carrying `id` plus whatever fields were fetched.
 */

import type { QueryInput } from "@/services/entity-instance.service";

export interface RelationItem {
  id: string;
  /** the related record's fetched fields (id + display/search fields) */
  data: Record<string, unknown>;
}

export function isRelationFieldType(fieldType: string | undefined | null): boolean {
  return !!fieldType && fieldType.endsWith("Data");
}

export function isListRelationFieldType(fieldType: string | undefined | null): boolean {
  return !!fieldType && fieldType.endsWith("ListData");
}

/** Target entity name encoded in the relation fieldType ("BrandData" -> "Brand"). */
export function relationTargetEntityName(fieldType: string): string {
  if (fieldType.endsWith("ListData")) return fieldType.slice(0, -"ListData".length);
  if (fieldType.endsWith("Data")) return fieldType.slice(0, -"Data".length);
  return fieldType;
}

/** Postgres can't text-match bigint; an empty `in` means no rows for non-bigint input. */
export function relationSearchQuery(
  field: string,
  dataType: string | undefined,
  search: string,
): QueryInput[] {
  if (String(dataType).toUpperCase() !== "INT") {
    return [{ condition: { field, operator: "ilike", value: `%${search}%` } }];
  }
  const value = search.trim();
  return [
    {
      condition: /^-?\d{1,18}$/.test(value)
        ? { field, operator: "equal", value }
        : { field, operator: "in", values: [] },
    },
  ];
}

interface RelationRowValue {
  data?: Record<string, unknown> | Array<Record<string, unknown>> | null;
}

/** Read the connected items (with their record data) for a relation off a row. */
export function readRelationItems(
  rowValue: Record<string, unknown> | undefined,
  fieldName: string,
  isList: boolean,
): RelationItem[] {
  const raw = (rowValue?.[fieldName] as RelationRowValue | undefined)?.data;
  const toItem = (o: Record<string, unknown>): RelationItem => ({ id: String(o.id), data: o });
  if (isList) return Array.isArray(raw) ? raw.map(toItem) : [];
  return raw && !Array.isArray(raw) ? [toItem(raw)] : [];
}

export interface RelationCellEdit {
  /** canonical comparable value (sorted ids) for dirty-detection */
  value: string[];
  /** original canonical value (sorted ids) */
  original: string[];
  /** relation-value-shaped preview the cell renders while dirty (carries record fields) */
  display: RelationRowValue;
  /** partial Update<Entity>Input fragment (set semantics) */
  input: Record<string, unknown>;
}

/**
 * Build the pending-edit pieces for setting a relation field to `items` (the
 * full intended set), using `_set` semantics. The display carries each item's
 * full record data so any column derived from this relation (type.id, type.name,
 * …) can render its own leaf field.
 */
export function buildRelationCellEdit(params: {
  items: RelationItem[];
  current: RelationItem[];
  fieldName: string;
  isList: boolean;
}): RelationCellEdit {
  const { items, current, fieldName, isList } = params;

  const input: Record<string, unknown> = isList
    ? { [`${fieldName}_set`]: items.map((i) => ({ id: i.id })) }
    : { [`${fieldName}_set`]: items[0] ? { id: items[0].id } : [] };

  const display: RelationRowValue = isList
    ? { data: items.map((i) => i.data) }
    : { data: items[0]?.data ?? null };

  return {
    value: items.map((i) => i.id).sort(),
    original: current.map((i) => i.id).sort(),
    display,
    input,
  };
}
